import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { CardTableSkeleton } from './TabSkeletons';
import { formatPercent as fmtPct } from '../../../../utils/formatters';

const scoreTone = (score) => {
  if (score < 60) return 'error';
  if (score < 75) return 'brand';
  if (score < 85) return 'warning';
  return 'success';
};

const ScoreBadge = ({ score }) => (
  <Badge tone={scoreTone(score)}>{(score ?? 0).toFixed(1)}</Badge>
);

const RankingTable = ({ rows, labelKey, displayKey }) => {
  const { t } = useTranslation('deliveryquality');
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="flex flex-col gap-2">
        {rows.map((r) => (
          <div key={r.key || 'sin'} className="rounded-lg border border-border p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-fg">
                {r[displayKey] || r.key || <em className="text-fg-subtle">{t('Sin valor')}</em>}
              </span>
              <ScoreBadge score={r.avg_score} />
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-fg-muted">
              <span>{t('# entregas')}: <strong className="text-fg tabular-nums">{r.total_evaluations}</strong></span>
              <span>{t('Tarde')}: <strong className="text-fg tabular-nums">{r.late_count}</strong></span>
              <span>{t('% Punt.')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.timeliness)}</strong></span>
              <span>{t('% Imág. IA')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.images_ai)}</strong></span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>{labelKey}</TH>
          <TH className="text-right">{t('# entregas')}</TH>
          <TH className="text-right">{t('Score prom')}</TH>
          <TH className="text-right">{t('% Punt.')}</TH>
          <TH className="text-right" title={t('% de entregas con verdict ENTREGA VALIDADA por la IA')}>{t('% Imág. IA')}</TH>
          <TH className="text-right">{t('Tarde')}</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((r) => (
          <TR key={r.key || 'sin'}>
            <TD>{r[displayKey] || r.key || <em className="text-fg-subtle">{t('Sin valor')}</em>}</TD>
            <TD className="text-right tabular-nums">{r.total_evaluations}</TD>
            <TD className="text-right"><ScoreBadge score={r.avg_score} /></TD>
            <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.timeliness)}</TD>
            <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.images_ai)}</TD>
            <TD className="text-right tabular-nums">{r.late_count}</TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
};

const TypeCompanyTab = ({ filters }) => {
  const { t } = useTranslation('deliveryquality');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [byType, setByType] = useState([]);
  const [byCompany, setByCompany] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([
      deliveryQualityService.getByType({ ...filters, limit: 50, sort_by: 'totalEvaluations', sort_dir: -1 }),
      deliveryQualityService.getByCompany({ ...filters, limit: 100, min_count: 5, sort_by: 'avgScore', sort_dir: 1 }),
    ])
      .then(([type, comp]) => {
        if (!alive) return;
        setByType(type.data?.data || []);
        setByCompany(comp.data?.data || []);
      })
      .catch((e) => alive && setError(e?.message || 'Error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filters]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-5"><CardTableSkeleton rows={8} columns={4} /></div>
        <div className="lg:col-span-7"><CardTableSkeleton rows={8} columns={4} /></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-status-error-soft bg-status-error-soft text-status-error px-4 py-3 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
      <div className="lg:col-span-5">
        <Card padding="none" className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-bg-muted text-sm font-semibold text-fg">
            {t('Por tipo de paquete ({{n}})', { n: byType.length })}
          </div>
          <div className="p-3 max-h-[600px] overflow-auto">
            {byType.length ? (
              <RankingTable rows={byType} labelKey={t('Tipo')} displayKey="key" />
            ) : (
              <div className="text-fg-muted text-sm">{t('Sin datos.')}</div>
            )}
          </div>
        </Card>
      </div>
      <div className="lg:col-span-7">
        <Card padding="none" className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-bg-muted text-sm font-semibold text-fg">
            {t('Por cliente / empresa ({{n}})', { n: byCompany.length })}
          </div>
          <div className="p-3 max-h-[600px] overflow-auto">
            {byCompany.length ? (
              <RankingTable rows={byCompany} labelKey={t('Cliente')} displayKey="company_name" />
            ) : (
              <div className="text-fg-muted text-sm">{t('Sin datos.')}</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TypeCompanyTab;
